import { CopyWellDetail, Point } from 'business/models/CustomModels';
import {
  CustomLayerDTO,
  GridDTO,
  GridLayerDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
  RandomLayerDTO,
} from 'business/models/Models';
import { IndexDBBuffer } from 'components/plateviewer/experiment/IndexDBBuffer';
import {
  addCustomLayerIngredients,
  addRandomLayerGroupIngredients,
  copyLayer,
  pasteAsNewLayer,
  SubstituteWithPreparedStock,
} from 'components/plateviewer/experiment/PasteLayerHelper';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useCallback } from 'react';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  setSelectedLayerId as updateSelectedLayerId,
  updateExperimentLayers,
  appendLayer,
  unDirtifyAlllayers,
} from 'redux/reducers/experimentDataReducer';
import { callGenerateLayer } from 'redux/reducers/experimentReducer';
import { selectExperimentLayers } from 'redux/selectors/ExperimentStateSelector';
import {
  checkConditionsAvailablity,
  setCopiedLayerName,
  setCopiedLayerPostion,
} from './utils';
import { useLastSelectedWellNavigationDetails } from 'redux/hooks/experiment';
import { useModalHandlers } from 'components/modal';
import { getNewId, showAlertSnackBar } from 'utils/Global';
import { getLinkedLayerGridDto } from 'business/plateviewer/GenerateUtil';
import { CutCopyOperationType } from 'components/Experiment/constants';

const BUFFER = new IndexDBBuffer();
let selectedWells: CopyWellDetail[] = [];
export interface usePasteParams {
  copyType: string;
  setShowPasteModal: (flag: boolean) => void;
  subStock: boolean;
  stocksNotFound: string[];
  setStocksNotFound: (s: string[]) => void;
  setShowStocksModal: (flag: boolean) => void;
  pasteNewLayer: boolean;
  selectedLayerType: LayerTypeDTO;
  selectedLayerId: number;
  layerOperationType: CutCopyOperationType;
}
function usePaste(params: usePasteParams) {
  const {
    setShowPasteModal,
    subStock,
    stocksNotFound,
    setStocksNotFound,
    setShowStocksModal,
    pasteNewLayer,
    selectedLayerType,
    selectedLayerId,
    layerOperationType,
  } = params;
  const dispatch = useRMDispatch();
  const exp = useRMSelector((state) => state.experiment.expData);
  const _setSelectedLayerId = useCallback(
    (id: number) => dispatch(updateSelectedLayerId(id)),
    [dispatch],
  );
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const _layers = useRMSelector(selectExperimentLayers);
  const _setLayers = useCallback(
    (lyrs) => dispatch(updateExperimentLayers(lyrs)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();
  const onPasteAsNewLayer = pasteAsNewLayer();
  const { showModal, hideModal } = useModalHandlers();
  const { wellNum, plateID } = wellNavigationDetails || {};

  const pasteLayerMenuEventHandler = useCallback(async () => {
    let copiedLayers: LayerDTO[] = [];
    copiedLayers = await BUFFER.ReadJSONLayers();
    const currentPlate = exp.plateDto.find((x) => x.plateId === plateID);
    let selectedLayerId;
    callPreserveUndoableState();
    dispatch(unDirtifyAlllayers());
    if (
      copiedLayers.length > 1 &&
      copiedLayers.every((x) => x.type === LayerTypeDTO.Grid)
    ) {
      let gridDto: GridDTO = getLinkedLayerGridDto(
        copiedLayers,
        copiedLayers[0]?.layerDetail,
      );
      const newCopiedLayers = [];
      const newGridId = getNewId();
      const parentGridLayer = copiedLayers.find(
        (layer) => (layer.layerDetail as GridLayerDTO).subGridNumber === 1,
      );
      copiedLayers
        .sort(
          (a, b) =>
            (a.layerDetail as GridLayerDTO).subGridNumber -
            (b.layerDetail as GridLayerDTO).subGridNumber,
        )
        .forEach((layer, index) => {
          const newCopiedLayer = copyLayer(layer, exp);
          if ((newCopiedLayer.layerDetail as GridLayerDTO).subGridNumber === 1) {
            gridDto = (newCopiedLayer.layerDetail as GridLayerDTO).grid;
            gridDto.id = newGridId;
            gridDto.gridIngredient.forEach((gIng) => {
              gIng.gridId = newGridId;
            });
          } else {
            (newCopiedLayer.layerDetail as GridLayerDTO).gridId = gridDto.id;
          }
          (newCopiedLayer.layerDetail as GridLayerDTO).gridId = newGridId;
          const diff: Point = {
            X: newCopiedLayer.x - parentGridLayer.x,
            Y: newCopiedLayer.y - parentGridLayer.y,
          };
          setCopiedLayerPostion(exp, newCopiedLayer, currentPlate, wellNum, diff);
          setCopiedLayerName(newCopiedLayer, exp, layerOperationType);
          newCopiedLayers.push(newCopiedLayer);
        });
      selectedLayerId = parentGridLayer.id;
      dispatch(updateExperimentLayers([..._layers, ...newCopiedLayers]));
    } else {
      const newCopiedLayer = copyLayer(copiedLayers[0], exp);

      if (newCopiedLayer !== null && currentPlate) {
        setCopiedLayerPostion(exp, newCopiedLayer, currentPlate, wellNum);
        setCopiedLayerName(newCopiedLayer, exp, layerOperationType);
        dispatch(appendLayer(newCopiedLayer));
        selectedLayerId = newCopiedLayer.id;
      }
    }
    dispatch(callGenerateLayer());
    _setSelectedLayerId(selectedLayerId);
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'Layer has been pasted.',
    );
  }, [BUFFER, _layers, _setSelectedLayerId, dispatch, exp, plateID, wellNum]);

  const pasteActionHandler = useCallback(async () => {
    const bufferCopyType = await BUFFER.ReadJSONCopyType();
    if (!bufferCopyType?.length) {
      return;
    }
    if (bufferCopyType[0].value === 'Well') {
      selectedWells = await BUFFER.ReadJSON();
      if (selectedWells && selectedWells.length > 0) {
        setShowPasteModal(true);
      }
    } else if (bufferCopyType[0].value === 'Layer') {
      await pasteLayerMenuEventHandler();
    }
  }, [BUFFER, pasteLayerMenuEventHandler, setShowPasteModal]);

  const pasteLayer = useCallback(() => {
    if (subStock && stocksNotFound.length == 0) {
      const stockList = SubstituteWithPreparedStock(selectedWells);
      if (stockList.length > 0) {
        setShowPasteModal(false);
        setStocksNotFound(stockList);
        setShowStocksModal(true);
        return;
      }
    }
    if (pasteNewLayer) {
      const expDto: any = onPasteAsNewLayer(
        selectedLayerType,
        exp,
        selectedWells,
        wellNum,
      );
      if (!expDto) {
        setShowPasteModal(false);
        return;
      }

      callPreserveUndoableState();
      dispatch(callGenerateLayer());
      setShowPasteModal(false);
    } else {
      const selectedLayer = exp.layers.find((lyr) => lyr.id == selectedLayerId);
      let success = false;
      let error = '';
      switch (selectedLayer.type) {
        case LayerTypeDTO.Random:
          addRandomLayerGroupIngredients(
            (selectedLayer.layerDetail as RandomLayerDTO).randomLayerIngredientGroup,
            selectedLayer.id,
            selectedWells,
          );
          success = true;
          break;
        case LayerTypeDTO.ConditionList:
          success = checkConditionsAvailablity(selectedLayer, selectedWells);
          if (success) {
            addCustomLayerIngredients(
              selectedLayer.layerDetail as CustomLayerDTO,
              selectedWells,
            );
          } else {
            error =
              'Not enough available conditions in layer. Please expand layer and try again.';
          }
          break;
      }
      if (success) {
        if (selectedLayer.layerState == LayerStateDTO.UnChanged) {
          selectedLayer.layerState = LayerStateDTO.Modified;
        }
        const tempLayers = [..._layers];
        const index = _layers.findIndex((l) => l.id == selectedLayer.id);
        tempLayers[index].dirty = true;
        tempLayers[index] = selectedLayer;
        callPreserveUndoableState();
        _setLayers(tempLayers);
        setShowPasteModal(false);
      } else {
        setShowPasteModal(false);
        showModal({
          body: error,
          heading: 'Error',
          onOk: hideModal,
          isOkOnly: true,
          okButtonText: 'OK',
        });
      }
    }
  }, [
    _layers,
    _setLayers,
    callPreserveUndoableState,
    exp,
    onPasteAsNewLayer,
    pasteNewLayer,
    selectedLayerId,
    selectedLayerType,
    setShowPasteModal,
    setShowStocksModal,
    setStocksNotFound,
    stocksNotFound,
    subStock,
    wellNum,
    selectedWells,
  ]);

  return [pasteActionHandler, pasteLayer];
}

export default usePaste;
